import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import MDButton from "components/MDButton";

const ALERT_KEY = "newFeaturesAlert_v4";
const RELEASE_DATE = new Date("2026-03-05");
const SHOW_DAYS = 5;

const NEW_FEATURES = [
  {
    label: "Activity Timeline",
    desc: "See all notes, reminders & file uploads for a lead in one unified feed on the lead detail page.",
  },
  {
    label: "Global Search  (Cmd+K / Ctrl+K)",
    desc: "Instantly search any lead by name, phone, email, company or status from anywhere in the app.",
  },
  {
    label: "Custom Fields",
    desc: "Admins can define custom text fields (e.g. Budget, Region) that appear on every lead. Set them up in Settings → Custom Fields.",
  },
  {
    label: "Improved Add Lead Form",
    desc: "The Add New Lead dialog now has a cleaner 2-column layout with all fields visible at once.",
  },
  {
    label: "Leads default to Last 3 Months",
    desc: "The leads table now loads the last 3 months of leads by default instead of last 1 month.",
  },
  {
    label: "Improved Lead Status Trend Chart",
    desc: "Smoother curves, area fills, better tooltips showing all statuses at once, and a cleaner grid.",
  },
  {
    label: "Redesigned Lead Funnel",
    desc: "The funnel now shows per-stage drop-off percentages, colored bars, and an overall conversion rate at a glance.",
  },
];

const NewFeaturesAlert = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(ALERT_KEY);
    if (dismissed) return;

    const now = new Date();
    const daysSince = (now - RELEASE_DATE) / (1000 * 60 * 60 * 24);
    if (daysSince <= SHOW_DAYS) {
      setOpen(true);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(ALERT_KEY, "dismissed");
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <FiberNewIcon sx={{ color: "#1A73E8", fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            What's New
          </Typography>
          <Chip
            label="Mar 2026"
            size="small"
            sx={{ ml: "auto", bgcolor: "#e8f0fe", color: "#1A73E8", fontWeight: 600, fontSize: "11px" }}
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
        <List disablePadding>
          {NEW_FEATURES.map((feature, i) => (
            <ListItem key={i} alignItems="flex-start" sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 32, mt: "3px" }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#1A73E8",
                    mt: "6px",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.25 }}>
                    {feature.label}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
                    {feature.desc}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={() => setOpen(false)}>
          Remind me later
        </MDButton>
        <MDButton variant="contained" color="info" onClick={handleDismiss}>
          Got it, don't show again
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default NewFeaturesAlert;
